import { useEffect, useState, useRef, useCallback } from "react";
import * as Icons from "lucide-react";
import { portalApi } from "@/lib/portalApi";
import { toast } from "sonner";

const fmtTime = (d) => (d ? new Date(d).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");

// Renders a single attachment: inline image for image/* types, download chip for everything else
function PortalAttachment({ attachment }) {
  const [downloading, setDownloading] = useState(false);
  const [imgError, setImgError] = useState(false);
  const isImage = (attachment.content_type || "").startsWith("image/");

  if (attachment.state !== "ready") {
    return (
      <span data-testid={`portal-attachment-unavailable-${attachment.id}`}
        className="inline-flex items-center gap-1 rounded border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] text-slate-500">
        <Icons.FileX className="h-3 w-3 shrink-0" aria-hidden="true" />
        <span className="truncate max-w-[120px]" title={attachment.filename}>{attachment.filename}</span>
        <span className="text-red-400 shrink-0">(Unavailable)</span>
      </span>
    );
  }

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const resp = await portalApi.get(attachment.download_path, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = attachment.filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download file."); }
    finally { setDownloading(false); }
  };

  // Inline image rendering with click-to-open viewer
  if (isImage && !imgError) {
    const viewerUrl = attachment.viewer_path;
    return (
      <div className="mt-1.5" data-testid={`portal-attachment-image-${attachment.id}`}>
        <a href={viewerUrl} target="_blank" rel="noopener noreferrer" aria-label={`View image: ${attachment.filename}`}>
          <img
            src={viewerUrl}
            alt={attachment.filename}
            onError={() => setImgError(true)}
            className="max-w-[280px] max-h-[210px] rounded-lg border border-slate-200 object-contain cursor-pointer hover:opacity-90 transition-opacity"
          />
        </a>
        <button type="button" onClick={handleDownload} disabled={downloading}
          className="text-[10px] text-slate-400 hover:text-slate-600 mt-0.5 inline-flex items-center gap-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F5A623]/60 rounded">
          {downloading ? <Icons.Loader2 className="h-3 w-3 animate-spin" /> : <Icons.Download className="h-3 w-3" />}
          {downloading ? "Downloading…" : "Download"}
        </button>
      </div>
    );
  }

  // Non-image or image load error → download chip (original AttachmentChip behavior)
  return (
    <button type="button" onClick={handleDownload} disabled={downloading}
      data-testid={`portal-attachment-${attachment.id}`}
      aria-label={`Download ${attachment.filename}`}
      className="inline-flex items-center gap-1 rounded border border-slate-200 bg-white px-2 py-1 text-[11px] text-slate-700 hover:bg-slate-50 hover:border-slate-300 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F5A623]/60">
      {downloading ? <Icons.Loader2 className="h-3 w-3 shrink-0 animate-spin" aria-hidden="true" /> : <Icons.Paperclip className="h-3 w-3 shrink-0" aria-hidden="true" />}
      <span className="truncate max-w-[120px] font-medium" title={attachment.filename}>{attachment.filename}</span>
      {!downloading && <Icons.Download className="h-3 w-3 text-slate-400 shrink-0" aria-hidden="true" />}
    </button>
  );
}

export default function PortalMessages() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [firstUnread, setFirstUnread] = useState(null);
  const scrollRef = useRef(null);
  const unreadRef = useRef(null);
  const markedRef = useRef(false);

  const scrollDown = () => setTimeout(() => scrollRef.current?.scrollTo({ top: 1e9, behavior: "smooth" }), 60);
  const scrollToUnread = () => setTimeout(() => {
    if (unreadRef.current) unreadRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    else scrollRef.current?.scrollTo({ top: 1e9, behavior: "smooth" });
  }, 80);

  // Outstanding-request guard: prevents overlapping fetches from polling + user action
  const fetchingRef = useRef(false);

  const load = useCallback(async () => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    try {
      const { data } = await portalApi.get("/portal/messages");
      setMessages(data.messages || []);
      setFirstUnread(data.first_unread_id || null);
    } catch { /* ignore */ }
    finally { setLoaded(true); fetchingRef.current = false; }
  }, []);

  // Initial load on mount
  useEffect(() => { load(); }, [load]);

  // 15-second polling so staff replies appear without navigation
  useEffect(() => {
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);

  // Immediate refresh when tab regains visibility
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [load]);
  useEffect(() => {
    if (!loaded) return;
    firstUnread ? scrollToUnread() : scrollDown();
    // Acknowledge unread once, after the thread (and its "New" divider) has rendered.
    if (firstUnread && !markedRef.current) {
      markedRef.current = true;
      portalApi.post("/portal/messages/mark-read").catch(() => {});
    }
  }, [loaded, firstUnread]); // eslint-disable-line

  const send = async () => {
    const body = input.trim();
    if (!body || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { id: `tmp${Date.now()}`, sender_type: "client", body, sent_at: new Date().toISOString() }]);
    scrollDown();
    try { await portalApi.post("/portal/messages", { body }); load(); }
    catch { toast.error("Message could not be sent."); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="portal-messages">
      <div>
        <h1 className="font-heading text-3xl font-bold text-[#171B2E]">Messages</h1>
        <p className="text-slate-500 text-sm mt-1">A secure, direct line to your tax team.</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col h-[60vh]">
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-3 tf-scroll" data-testid="portal-message-thread">
          {!loaded ? (
            <div className="flex justify-center py-10"><Icons.Loader2 className="h-5 w-5 animate-spin text-[#F5A623]" /></div>
          ) : messages.length === 0 ? (
            <div className="text-center text-slate-400 mt-12">
              <Icons.MessageSquare className="h-10 w-10 mx-auto mb-2 text-slate-300" />
              <p className="text-sm">No messages yet. Say hello to your tax team!</p>
            </div>
          ) : (
            messages.filter((m) => m.sender_type !== "penny").map((m) => {
              const mine = m.sender_type === "client";
              const isUnread = m.id === firstUnread;
              return (
                <div key={m.id} ref={isUnread ? unreadRef : null} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className="max-w-[75%]">
                    {isUnread && (
                      <div className="flex items-center gap-2 my-1" data-testid="portal-unread-divider">
                        <div className="flex-1 h-px bg-blue-300" />
                        <span className="text-[10px] font-semibold text-blue-600 uppercase tracking-wide">New</span>
                        <div className="flex-1 h-px bg-blue-300" />
                      </div>
                    )}
                    {!mine && <div className="text-[11px] text-slate-400 mb-0.5 ml-1">{m.sender_name || "Your team"}</div>}
                    <div className={`rounded-2xl px-3.5 py-2 text-sm leading-relaxed ${mine ? "bg-[#171B2E] text-white rounded-tr-sm" : `bg-slate-100 text-slate-700 rounded-tl-sm ${isUnread ? "ring-2 ring-blue-300" : ""}`}`}>{m.body}</div>
                    {/* Attachment chips / inline images — only shown when staff has included attachments */}
                    {m.attachments && m.attachments.length > 0 && (
                      <div className={`mt-1.5 flex flex-wrap gap-2 ${mine ? "justify-end" : ""}`}
                        role="list" aria-label={`Attachments in message from ${m.sender_name || "your team"}`}>
                        {m.attachments.map((att) => (
                          <div key={att.id} role="listitem"><PortalAttachment attachment={att} /></div>
                        ))}
                      </div>
                    )}
                    <div className={`text-[10px] text-slate-400 mt-0.5 ${mine ? "text-right mr-1" : "ml-1"}`}>{fmtTime(m.sent_at)}</div>
                  </div>
                </div>
              );
            })
          )}
        </div>
        <div className="p-3 border-t border-slate-100">
          <div className="flex items-center gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
              data-testid="portal-message-input" placeholder="Write a message to your tax team…"
              className="flex-1 bg-[#F6F7FB] border border-slate-200 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#F5A623]/40" />
            <button onClick={send} disabled={busy || !input.trim()} data-testid="portal-message-send"
              className="h-10 px-4 flex items-center gap-2 rounded-lg bg-[#F5A623] hover:bg-[#e0951a] disabled:opacity-50 text-[#171B2E] font-semibold text-sm"><Icons.Send className="h-4 w-4" />Send</button>
          </div>
        </div>
      </div>
    </div>
  );
}
